import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { ensureCurrentUser } from '../../util/data';
import { storableError } from '../../util/errors';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';

const DASHBOARD_TX_QUERY_SIZE = 100;
const { UUID } = sdkTypes;

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Async thunks ================ //

const fetchTransactionsPayloadCreator = (
  { page = 1, perPage = DASHBOARD_TX_QUERY_SIZE, only = 'order', bookingStart = null, bookingEnd = null },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const apiQueryParams = {
    only,
    processNames,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'booking',
      'customer',
      'customer.profileImage',
      'reviews',
      'reviews.author',
      'reviews.subject',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'metadata',
      'protectedData',
    ],
    'fields.review': ['rating', 'deleted'],
    'fields.listing': [
      'title',
      'availabilityPlan',
      'price',
      'metadata',
      'description',
      'geolocation',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.category',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.categoryLevel3',
      'publicData.sessionPricing',
      'publicData.priceVariants',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.firstName',
      'metadata',
      'deleted',
      'banned',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage,
    ...(bookingStart ? { bookingStart } : {}),
    ...(bookingEnd ? { bookingEnd } : {}),
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchTransactionsThunk = createAsyncThunk(
  'DashboardPage/fetchTransactions',
  fetchTransactionsPayloadCreator
);

const fetchFavoriteListingsPayloadCreator = ({ listingIds }, { dispatch, rejectWithValue, extra: sdk }) => {
  const ids = Array.isArray(listingIds) ? listingIds.filter(id => typeof id === 'string') : [];
  if (ids.length === 0) {
    return Promise.resolve({ data: [] });
  }

  const showParams = id => ({
    id: new UUID(id),
    expand: true,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'price',
      'metadata',
      'description',
      'geolocation',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.category',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.categoryLevel3',
      'publicData.sessionPricing',
      'publicData.priceVariants',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'metadata', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  });

  return Promise.allSettled(ids.map(id => sdk.listings.show(showParams(id))))
    .then(results => {
      const successfulResponses = results
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value);

      successfulResponses.forEach(r => dispatch(addMarketplaceEntities(r)));

      const listings = successfulResponses
        .map(r => r?.data?.data)
        .filter(d => d && d.id && d.type);

      const firstError = results.find(r => r.status === 'rejected')?.reason;
      if (listings.length === 0 && firstError) {
        return rejectWithValue(storableError(firstError));
      }

      return { data: listings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchFavoriteListingsThunk = createAsyncThunk(
  'DashboardPage/fetchFavoriteListings',
  fetchFavoriteListingsPayloadCreator
);

// ================ Slice ================ //

const dashboardPageSlice = createSlice({
  name: 'DashboardPage',
  initialState: {
    fetchTransactionsInProgress: false,
    fetchTransactionsError: null,
    transactionRefs: [],
    transactionsPagination: null,

    fetchFavoriteListingsInProgress: false,
    fetchFavoriteListingsError: null,
    favoriteListingRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // fetchTransactions
      .addCase(fetchTransactionsThunk.pending, state => {
        state.fetchTransactionsInProgress = true;
        state.fetchTransactionsError = null;
      })
      .addCase(fetchTransactionsThunk.fulfilled, (state, action) => {
        const transactions = action.payload.data.data;
        state.fetchTransactionsInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.transactionsPagination = action.payload.data.meta;
      })
      .addCase(fetchTransactionsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchTransactionsInProgress = false;
        state.fetchTransactionsError = action.payload;
      })
      // fetchFavoriteListings
      .addCase(fetchFavoriteListingsThunk.pending, state => {
        state.fetchFavoriteListingsInProgress = true;
        state.fetchFavoriteListingsError = null;
      })
      .addCase(fetchFavoriteListingsThunk.fulfilled, (state, action) => {
        const listings = action.payload?.data || [];
        state.fetchFavoriteListingsInProgress = false;
        state.favoriteListingRefs = entityRefs(listings);
      })
      .addCase(fetchFavoriteListingsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchFavoriteListingsInProgress = false;
        state.fetchFavoriteListingsError = action.payload;
      });
  },
});

export default dashboardPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUser())
    .then(() => {
      const currentUser = ensureCurrentUser(getState()?.user?.currentUser);
      const roles = getCurrentUserTypeRoles(config, currentUser);
      const only = roles?.customer ? 'order' : 'sale';
      return dispatch(fetchTransactionsThunk({ page: 1, only }));
    })
    .catch(e => {
      throw e;
    });
};
